import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    InputAdornment,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconSearch, IconHistory, IconAdjustments, IconBoxSeam, IconUpload } from '@tabler/icons';
import debounce from 'lodash.debounce';
import { useHistory } from 'react-router-dom';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import ImportDialog from '../../../../../ui-component/extended/ImportDialog';
import PaginationBar from '../../../../../ui-component/extended/PaginationBar';
import LoadingBox from '../../../../../ui-component/extended/LoadingBox';
import useRequestGuard from '../../../../../hooks/useRequestGuard';

const StockPosition = () => {
    const history = useHistory();
    const { start, isCurrent } = useRequestGuard();

    const [appId, setAppId] = useState(null);
    const [stock, setStock] = useState([]);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ count: 0, numPages: 1 });
    const [loading, setLoading] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [warehouseFilter, setWarehouseFilter] = useState('');
    const [importDialogOpen, setImportDialogOpen] = useState(false);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        (async () => {
            const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
            setWarehouses(res.data || []);
        })();
    }, [appId]);

    const loadStock = async () => {
        if (!appId) return;
        const requestId = start();
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (warehouseFilter) params.set('warehouse', warehouseFilter);
            if (debouncedSearch) params.set('search', debouncedSearch);
            params.set('page', page);
            const res = await api.get(`v1/apps/${appId}/inventory/stock/?${params.toString()}`);
            if (!isCurrent(requestId)) return;
            setStock(res.data.stock || []);
            setPagination({ count: res.data.count || 0, numPages: res.data.num_pages || 1 });
        } finally {
            if (isCurrent(requestId)) setLoading(false);
        }
    };

    useEffect(() => {
        loadStock();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, warehouseFilter, debouncedSearch, page]);

    const debouncedSetSearch = useMemo(
        () =>
            debounce((value) => {
                setPage(1);
                setDebouncedSearch(value);
            }, 400),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSetSearch(value);
    };

    return (
        <Box>
            <PageHeader
                icon={IconBoxSeam}
                gradient="amber"
                title="Estoque"
                subtitle="Posição atual por variação de produto"
                actions={
                    <Stack direction="row" spacing={2}>
                        <TextField
                            select
                            size="small"
                            label="Depósito"
                            value={warehouseFilter}
                            onChange={(e) => {
                                setWarehouseFilter(e.target.value);
                                setPage(1);
                            }}
                            sx={{ width: 180 }}
                        >
                            <MenuItem value="">Todos (agregado)</MenuItem>
                            {warehouses.map((w) => (
                                <MenuItem key={w.id} value={w.id}>
                                    {w.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            placeholder="Buscar produto/SKU..."
                            value={search}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                )
                            }}
                            sx={{ width: 240 }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<IconHistory size={18} />}
                            onClick={() => history.push('/dashboard/inventory/movements')}
                        >
                            Histórico
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<IconAdjustments size={18} />}
                            onClick={() => history.push('/dashboard/inventory/new')}
                        >
                            Novo Movimento
                        </Button>
                        <Button variant="outlined" startIcon={<IconUpload size={18} />} onClick={() => setImportDialogOpen(true)}>
                            Importar saldo inicial
                        </Button>
                    </Stack>
                }
            />

            <Card>
                <CardContent>
                    {loading ? (
                        <LoadingBox label="Carregando estoque..." />
                    ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Produto</TableCell>
                                <TableCell>Variação</TableCell>
                                <TableCell>SKU</TableCell>
                                <TableCell align="right">{warehouseFilter ? 'Estoque no depósito' : 'Estoque (todos os depósitos)'}</TableCell>
                                {!!warehouseFilter && <TableCell align="right">Mín / Máx</TableCell>}
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {stock.map((item) => (
                                <TableRow key={item.produto_variacao}>
                                    <TableCell>{item.produto}</TableCell>
                                    <TableCell>{item.variacao}</TableCell>
                                    <TableCell>{item.sku || '-'}</TableCell>
                                    <TableCell align="right">
                                        {item.low_stock ? (
                                            <Chip label={`${item.estoque} - baixo`} color="warning" size="small" />
                                        ) : (
                                            item.estoque
                                        )}
                                    </TableCell>
                                    {!!warehouseFilter && (
                                        <TableCell align="right">
                                            {item.estoque_minimo ?? '—'} / {item.estoque_maximo ?? '—'}
                                        </TableCell>
                                    )}
                                    <TableCell align="right">
                                        <Button
                                            size="small"
                                            onClick={() =>
                                                history.push(
                                                    `/dashboard/inventory/movements?produto_variacao=${item.produto_variacao}${
                                                        warehouseFilter ? `&warehouse=${warehouseFilter}` : ''
                                                    }`
                                                )
                                            }
                                        >
                                            Histórico
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {stock.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={warehouseFilter ? 6 : 5} align="center">
                                        Nenhuma variação encontrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    )}
                </CardContent>
            </Card>

            <PaginationBar page={page} numPages={pagination.numPages} count={pagination.count} onChange={setPage} />

            <ImportDialog
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                title="Importar Saldo Inicial de Estoque"
                entityLabel="linhas de saldo"
                systemFields={[
                    { key: 'sku', label: 'SKU (ou use nome_produto+nome_variacao)', required: false },
                    { key: 'nome_produto', label: 'Nome do Produto (se não usar SKU)', required: false },
                    { key: 'nome_variacao', label: 'Nome da Variação (se não usar SKU)', required: false },
                    { key: 'deposito', label: 'Depósito (precisa já existir)', required: true },
                    { key: 'quantidade', label: 'Quantidade', required: true }
                ]}
                fieldsEndpoint={`v1/apps/${appId}/inventory/movement_fields/`}
                importUrl={`v1/apps/${appId}/inventory/stock/import/`}
                onImported={loadStock}
            />
        </Box>
    );
};

export default StockPosition;
